import { useRef, useState, type ReactNode } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Monitor, TerminalSquare } from "lucide-react";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  getHostLabel,
  getInstallSkillPacks,
  getTokenCommand,
  QUICK_START_SETUP_COMMANDS,
} from "@/lib/chat/cli-guidance";
import { cn } from "@/lib/utils";
import type { Platform } from "@/stores/chat-store";
import type { HelpStep, HelpStepComponentProps } from "../help-flows";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function Surface({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">{children}</div>;
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Surface>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">{title}</p>
      <div className="mt-2 text-sm leading-6 text-foreground/90">{children}</div>
    </Surface>
  );
}

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Troubleshooting({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-8 space-y-3 border-t border-white/[0.06] pt-6">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Need a fallback?
      </button>
      {open ? <Surface>{children}</Surface> : null}
    </div>
  );
}

function InlineCliErrorAlert({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-200">{title}</p>
          <div className="mt-1.5 space-y-2 text-sm leading-6 text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Terminal choice — IDE integrated vs standalone app                        */
/* -------------------------------------------------------------------------- */

type TerminalChoice = "standalone" | "ide";

function TerminalChoicePicker({
  choice,
  onChange,
}: {
  choice: TerminalChoice;
  onChange: (value: TerminalChoice) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <button
        type="button"
        onClick={() => {
          onChange("ide");
        }}
        className={cn(
          "flex items-start gap-4 rounded-xl border p-5 text-left transition-all hover:border-white/[0.12] hover:bg-white/[0.05]",
          choice === "ide"
            ? "border-white/[0.15] bg-white/[0.06] ring-1 ring-white/[0.08]"
            : "border-white/[0.06] bg-white/[0.03]"
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-blue-500/10 text-blue-400">
          <Monitor className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">IDE terminal</p>
          <p className="text-xs leading-5 text-muted-foreground">
            Use the built-in terminal in VS Code, Cursor, or your IDE. Press{" "}
            <kbd className="rounded border border-white/[0.1] bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px]">Ctrl + `</kbd>{" "}
            to toggle it.
          </p>
        </div>
      </button>

      <button
        type="button"
        onClick={() => {
          onChange("standalone");
        }}
        className={cn(
          "flex items-start gap-4 rounded-xl border p-5 text-left transition-all hover:border-white/[0.12] hover:bg-white/[0.05]",
          choice === "standalone"
            ? "border-white/[0.15] bg-white/[0.06] ring-1 ring-white/[0.08]"
            : "border-white/[0.06] bg-white/[0.03]"
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-violet-500/10 text-violet-400">
          <TerminalSquare className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">Standalone terminal</p>
          <p className="text-xs leading-5 text-muted-foreground">
            A separate Terminal, PowerShell, or Command Prompt window outside of any editor.
          </p>
        </div>
      </button>
    </div>
  );
}

function OpenTerminalGuide({
  choice,
  platform,
}: {
  choice: TerminalChoice;
  platform: Platform;
}) {
  if (choice === "ide") {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-foreground">VS Code / Cursor</p>
            <p className="text-sm leading-6 text-muted-foreground">
              Press{" "}
              <kbd className="rounded border border-white/[0.1] bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs">Ctrl + `</kbd>{" "}
              or open <span className="font-medium text-foreground/80">Terminal → New Terminal</span>.
            </p>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-foreground">Other editors</p>
            <p className="text-sm leading-6 text-muted-foreground">
              Look for <span className="font-medium text-foreground/80">Terminal</span> or <span className="font-medium text-foreground/80">Console</span> in the menu.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (platform === "mac") {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-foreground">macOS</p>
          <p className="text-sm leading-6 text-muted-foreground">
            Press{" "}
            <kbd className="rounded border border-white/[0.1] bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs">⌘ + Space</kbd>,
            type <span className="font-medium text-foreground/80">"Terminal"</span>, then press Enter.
          </p>
        </div>
      </div>
    );
  }

  const appName = platform === "windows-cmd" ? "Command Prompt" : "PowerShell";
  const searchTerm = platform === "windows-cmd" ? "cmd" : "PowerShell";

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">Windows</p>
        <p className="text-sm leading-6 text-muted-foreground">
          Press the{" "}
          <kbd className="rounded border border-white/[0.1] bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs">Win</kbd>{" "}
          key, type <span className="font-medium text-foreground/80">"{searchTerm}"</span>, then press Enter to open {appName}.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step components                                                           */
/* -------------------------------------------------------------------------- */

function RefreshPackagesStep(_props: Pick<HelpStepComponentProps, "onAdvance">) {
  const { selectedPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const [terminalChoice, setTerminalChoice] = useState<TerminalChoice>("ide");

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Where will you run commands?">
        <p className="text-sm leading-6 text-muted-foreground">
          Pick the window you want to use. You will paste the same commands either way.
        </p>
        <TerminalChoicePicker choice={terminalChoice} onChange={setTerminalChoice} />
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Open your terminal">
        <p className="text-sm leading-6 text-muted-foreground">
          A terminal is just a window where you type commands. Open the one you picked below.
        </p>
        <OpenTerminalGuide choice={terminalChoice} platform={platform} />
      </NumberedInstruction>

      <NumberedInstruction step={3} title="Paste this command">
        <p className="text-sm leading-6 text-muted-foreground">
          Copy this command and paste it into your terminal. It tells GitHub to let this machine download SpecTree's private packages.
        </p>
        <CommandCopyBox
          label="Run in your terminal"
          command={QUICK_START_SETUP_COMMANDS.refreshPackagesAccess}
          helperText="GitHub opens in your browser so you can approve package downloads."
        />
      </NumberedInstruction>

      <NumberedInstructionLast step={4} title="What happens next">
        <p className="text-sm leading-6 text-muted-foreground">
          GitHub opens in your browser. Sign in, click approve, then come back to your terminal. You should see something like this:
        </p>
        <CopyableTerminal title="Expected output">
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              {QUICK_START_SETUP_COMMANDS.refreshPackagesAccess}
            </p>
            <p className="text-muted-foreground/70">Opening github.com so you can sign in…</p>
            <p className="text-emerald-400">✓ Authentication complete. Press Enter to continue...</p>
          </div>
        </CopyableTerminal>

        <InlineCliErrorAlert title="Common CLI error: npm ERR! 401 / 403 from GitHub Packages">
          <p>
            If a later <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npm install</code>{" "}
            or <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npx @ttc-spectree/spectree-mcp</code>{" "}
            run shows <span className="font-medium text-foreground/90">401 Unauthorized</span> or{" "}
            <span className="font-medium text-foreground/90">403 Forbidden</span> from{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npm.pkg.github.com</code>,
            GitHub Packages permission was not refreshed successfully.
          </p>
          <p>
            Fix: rerun this step, approve the browser prompt for the{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">read:packages</code>{" "}
            scope, then continue in the same terminal window.
          </p>
        </InlineCliErrorAlert>
      </NumberedInstructionLast>
    </div>
  );
}

function ConfigureRegistryStep(_props: Pick<HelpStepComponentProps, "onAdvance">) {
  const { selectedPlatform } = useOnboarding();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Stay in the same terminal">
        <p className="text-sm leading-6 text-muted-foreground">
          Keep using the same terminal window. This step tells npm where SpecTree's private packages live so future installs know where to look.
        </p>
        <Callout title="Before / after">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.04] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                Before
              </p>
              <p className="mt-2 text-sm leading-6 text-foreground/90">
                npm only checks the public npm website.
              </p>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.04] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                After
              </p>
              <p className="mt-2 text-sm leading-6 text-foreground/90">
                npm knows that packages that start with{" "}
                <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">@ttc-spectree</code>{" "}
                come from GitHub Packages.
              </p>
            </div>
          </div>
        </Callout>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Paste this command">
        <p className="text-sm leading-6 text-muted-foreground">
          Paste this once and press Enter. It saves the right download address for SpecTree's private packages.
        </p>
        <CommandCopyBox
          label="Run in your terminal"
          command={QUICK_START_SETUP_COMMANDS.configureGithubPackages}
          helperText="This only changes where @ttc-spectree packages come from."
        />

        {selectedPlatform === "windows-powershell" && (
        <InlineCliErrorAlert title="Windows PowerShell: 'running scripts is disabled on this system'">
          <p>
            If you see{" "}
            <span className="font-medium text-foreground/90">npm.ps1 cannot be loaded because running scripts is disabled on this system</span>,
            PowerShell is blocking npm from running. Paste this command first, then answer <strong>Y</strong> when prompted:
          </p>
          <CommandCopyBox
            label="Run in PowerShell"
            command="Set-ExecutionPolicy -Scope CurrentUser -ExecutionPolicy RemoteSigned"
            helperText="This only changes your user account — it does not affect other users on the machine."
          />
          <p>
            After that, retry the npm command that failed.
          </p>
        </InlineCliErrorAlert>
        )}
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What you should see">
        <p className="text-sm leading-6 text-muted-foreground">
          This command is usually quiet. npm just saves the new location for later.
        </p>
        <CopyableTerminal title="What you should see">
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              {QUICK_START_SETUP_COMMANDS.configureGithubPackages}
            </p>
            <p className="text-muted-foreground/70">
              npm saves the new download location for later.
            </p>
          </div>
        </CopyableTerminal>
      </NumberedInstructionLast>
    </div>
  );
}

function SetAuthTokenStep(_props: Pick<HelpStepComponentProps, "onAdvance">) {
  const { selectedPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const tokenCommand = getTokenCommand(platform);

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Stay in the same terminal">
        <p className="text-sm leading-6 text-muted-foreground">
          Now npm knows where to look. This step saves your GitHub sign-in token so private package downloads know it is really you.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Paste this command">
        <p className="text-sm leading-6 text-muted-foreground">
          Paste this command and press Enter. It quietly saves your GitHub token in npm.
        </p>
        <CommandCopyBox
          label="Run in your terminal"
          command={tokenCommand}
          helperText={
            platform === "windows-cmd"
              ? "Use this version if you picked Command Prompt on Windows."
              : "Use this version in Mac Terminal, PowerShell, bash, or zsh."
          }
        />
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What you should see">
        <p className="text-sm leading-6 text-muted-foreground">
          npm saves the token right away. Future installs can use it without asking you to sign in again.
        </p>
        <CopyableTerminal title="What you should see">
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              {tokenCommand}
            </p>
            <p className="text-muted-foreground/70">
              npm saves your token with no extra questions.
            </p>
          </div>
        </CopyableTerminal>

        <InlineCliErrorAlert title="Common CLI error: npm ERR! 401 / 403 from GitHub Packages">
          <p>
            If <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npx @ttc-spectree/spectree-mcp</code>{" "}
            or <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npm install</code>{" "}
            still reports <span className="font-medium text-foreground/90">401 Unauthorized</span> or{" "}
            <span className="font-medium text-foreground/90">403 Forbidden</span> from{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">npm.pkg.github.com</code>,
            npm usually does not have a valid GitHub Packages token yet.
          </p>
          <p>
            Fix: confirm you're signed in with{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">gh auth status</code>,
            rerun this token command in the same terminal, then retry the install.
          </p>
        </InlineCliErrorAlert>
      </NumberedInstructionLast>
    </div>
  );
}

export function InstallSkillPacksStep(_props: Pick<HelpStepComponentProps, "onAdvance">) {
  const host = useResolvedHost();
  const installPreview = "cd your-project && " + getInstallSkillPacks(host);

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Navigate to your project folder">
        <p className="text-sm leading-6 text-muted-foreground">
          In the same terminal, move into your project's main folder first. This is the folder where{" "}
          <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">git status</code>{" "}
          shows your repository.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Paste this command">
        <p className="text-sm leading-6 text-muted-foreground">
          Once you are in the project folder, paste this command. It adds the SpecTree helpers to your repo so{" "}
          {getHostLabel(host)} can find them later.
        </p>
        <CommandCopyBox
          label="Run in your project directory"
          command={getInstallSkillPacks(host)}
          helperText="Open the main project folder first, then run this once."
        />
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What happens next">
        <p className="text-sm leading-6 text-muted-foreground">
          The install writes two layouts to your repo: <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">.github/</code> for
          GitHub Copilot CLI and <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">.claude/</code> for Claude Code. Seeing
          both is expected even if you only work in {getHostLabel(host)} — the install always provisions both hosts.
        </p>
        <CopyableTerminal title="Expected output" copyText={installPreview}>
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              {installPreview}
            </p>
            <p className="text-muted-foreground/70">
              Installed @spectree/full (copilot), @spectree/claude (claude)
            </p>
          </div>
        </CopyableTerminal>
      </NumberedInstructionLast>

      <Troubleshooting>
        <div className="space-y-2 text-sm leading-6 text-muted-foreground">
          <p>
            Confirm you are at the repository root with{" "}
            <code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">git status</code>.
          </p>
          <p>If the install returns 401, repeat the auth token step and retry.</p>
        </div>
      </Troubleshooting>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Exported step definitions                                                 */
/* -------------------------------------------------------------------------- */

export function getCLISetupSteps(_platform: Platform): HelpStep[] {
  return [
    {
      id: "refresh-packages",
      title: "Refresh package access",
      description: "Reconnect GitHub CLI so private package downloads work.",
      component: RefreshPackagesStep,
    },
    {
      id: "configure-registry",
      title: "Point npm to the right download spot",
      description: "Tell npm where to find SpecTree's private packages.",
      component: ConfigureRegistryStep,
    },
    {
      id: "set-auth-token",
      title: "Save your sign-in token",
      description: "Save your GitHub sign-in token in npm so installs keep working.",
      component: SetAuthTokenStep,
    },
    {
      id: "install-skill-packs",
      title: "Install the skill packs",
      description: "Add the SpecTree helpers to your repository so your CLI host can use them.",
      component: InstallSkillPacksStep,
    },

  ];
}
